/**
 * Audit-log filter URL state
 *
 * The audit-log filter controls (search, event type, status, date range, and
 * histogram time-range drill-down) persist their active values in the URL
 * hash query string so the filtered view is bookmarkable and shareable.
 *
 * Works in both standalone (`#/audit-logs?...`) and embedded-in-Customer-360
 * (`#/customer-360?customerId=X&tab=audit&...`) contexts because the param
 * names chosen here don't collide with Customer 360's own hash namespace
 * (customerId, tab, view, user).
 *
 * Param schema:
 *   q       — unified search string ($or across user.email, ipAddress, userAgent)
 *   evt     — event type value. Three shapes:
 *               - single event type: "LOGIN_FAILED"
 *               - category: "CAT:AUTHENTICATION"
 *               - comma-joined multi: "LOGIN,LOGOUT"
 *   status  — "success" | "failure"
 *   from    — datetime-local string ("YYYY-MM-DDTHH:mm")
 *   to      — datetime-local string ("YYYY-MM-DDTHH:mm")
 *   tStart  — ISO timestamp pinned by histogram drill-down
 *   tEnd    — ISO timestamp pinned by histogram drill-down
 *   customer — customerId (preserved from legacy customer deep-links)
 *   page    — 1-based page number (preserved from legacy pagination links)
 *
 * Legacy param names still accepted on read (translated to the new schema):
 *   eventTypes=A,B   → evt=A,B
 *   category=X       → evt=CAT:X
 *   timeStart/timeEnd → tStart/tEnd
 */

const AUDIT_FILTER_KEYS = [
    'q',
    'evt',
    'status',
    'from',
    'to',
    'tStart',
    'tEnd',
    'customer',
    'page',
];

function splitHash(hash) {
    const h = hash || '';
    const qIdx = h.indexOf('?');
    return {
        route: qIdx === -1 ? h : h.slice(0, qIdx),
        query: qIdx === -1 ? '' : h.slice(qIdx + 1),
    };
}

/**
 * Read audit-log filter state from a hash string (defaults to the current
 * window's hash). Never throws — returns empty fields for anything missing.
 */
export function readAuditFilters(hash = window.location.hash) {
    const { query } = splitHash(hash);
    const p = new URLSearchParams(query);

    // Translate legacy params if present and the new names aren't.
    let evt = p.get('evt') || '';
    if (!evt) {
        const legacyEventTypes = p.get('eventTypes');
        const legacyCategory = p.get('category');
        if (legacyCategory) {
            evt = `CAT:${legacyCategory}`;
        } else if (legacyEventTypes) {
            evt = legacyEventTypes;
        }
    }

    return {
        q: p.get('q') || '',
        evt,
        status: p.get('status') || '',
        from: p.get('from') || '',
        to: p.get('to') || '',
        tStart: p.get('tStart') || p.get('timeStart') || '',
        tEnd: p.get('tEnd') || p.get('timeEnd') || '',
        customer: p.get('customer') || '',
        page: p.get('page') || '',
    };
}

/**
 * Custom event fired after writeAuditFilters updates the URL. history.pushState
 * / replaceState do not trigger the native `hashchange` event, so anyone
 * listening for filter updates (e.g. log-filters.tag) should listen to both
 * events — `hashchange` for browser back/forward, this one for in-app writes.
 */
export const AUDIT_FILTERS_EVENT = 'cloudcore:audit-filters-changed';

/**
 * Write audit-log filter state to the URL hash, preserving any non-filter
 * params the caller (e.g. Customer 360) owns on the same hash.
 *
 * options.replace — use history.replaceState instead of pushState (default push).
 */
export function writeAuditFilters(filters, options = {}) {
    const { route, query } = splitHash(window.location.hash);
    const p = new URLSearchParams(query);

    // Strip every filter key (including legacy ones) so stale values don't
    // linger when we re-write. Non-filter params (tab, customerId, user,
    // view, etc.) pass through untouched.
    for (const key of [...AUDIT_FILTER_KEYS, 'eventTypes', 'category', 'timeStart', 'timeEnd']) {
        p.delete(key);
    }

    // Add back only the filter fields that have values.
    for (const key of AUDIT_FILTER_KEYS) {
        const value = filters?.[key];
        if (value !== undefined && value !== null && value !== '') {
            p.set(key, value);
        }
    }

    const qs = p.toString();
    const newHash = qs ? `${route}?${qs}` : route;
    if (newHash === window.location.hash) return;

    const method = options.replace ? 'replaceState' : 'pushState';
    window.history[method](null, '', newHash);
    window.dispatchEvent(new CustomEvent(AUDIT_FILTERS_EVENT));
}

/**
 * True if the filters object has any scoping beyond the default sort.
 * Mirrors the old heavy-card's hasActiveFilters helper.
 */
export function hasAuditFilters(filters) {
    if (!filters) return false;
    return AUDIT_FILTER_KEYS.some((key) => {
        const value = filters[key];
        return value !== undefined && value !== null && value !== '';
    });
}

/**
 * Shape an empty filter state — useful as the reset default and for
 * comparisons. `page` is omitted here because the filter row doesn't own it.
 */
export function emptyAuditFilters() {
    return {
        q: '',
        evt: '',
        status: '',
        from: '',
        to: '',
        tStart: '',
        tEnd: '',
        customer: '',
    };
}

/**
 * Modal-state hash key for the audit log detail dialog. Lives in the same
 * hash query string as the filters, but kept out of AUDIT_FILTER_KEYS so
 * filter writes don't strip it and so it doesn't show up in filter exports
 * or "are filters active" checks. Doesn't collide with Customer 360's
 * (customerId / tab / view / user) namespace.
 */
const AUDIT_MODAL_KEY = 'auditlog';

/**
 * Read the currently-open audit log id from the hash, or '' if no modal
 * is requested.
 */
export function readAuditModal(hash = window.location.hash) {
    const { query } = splitHash(hash);
    const p = new URLSearchParams(query);
    return p.get(AUDIT_MODAL_KEY) || '';
}

/**
 * Write (or clear) the modal-open hash key. Pass null/empty string to clear.
 * Preserves all other params (filters, Customer 360 keys) untouched.
 *
 * options.replace — use replaceState instead of pushState (default push, so
 *   browser back closes the modal naturally).
 */
export function writeAuditModal(logId, options = {}) {
    const { route, query } = splitHash(window.location.hash);
    const p = new URLSearchParams(query);

    if (logId) {
        p.set(AUDIT_MODAL_KEY, logId);
    } else {
        p.delete(AUDIT_MODAL_KEY);
    }

    const qs = p.toString();
    const newHash = qs ? `${route}?${qs}` : route;
    if (newHash === window.location.hash) return;

    const method = options.replace ? 'replaceState' : 'pushState';
    window.history[method](null, '', newHash);
}
